import React, { createContext, useContext, useState, useEffect } from 'react';
import { tours as initialTours, initialAvailability, STAFF_PASSWORD } from '../data/mockData';
import { supabase } from '../lib/supabase';


const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};


export const AppProvider = ({ children }) => {

  const [tours, setTours] = useState(initialTours);
  const [availability, setAvailability] = useState({});

  const [isStaffLoggedIn, setIsStaffLoggedIn] = useState(() => {
    return localStorage.getItem('staffLoggedIn') === 'true';
  });

  const [bookings, setBookings] = useState(() => {
    const saved = localStorage.getItem('tourBookings');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('staffLoggedIn', isStaffLoggedIn.toString());
  }, [isStaffLoggedIn]);

  useEffect(() => {
  fetchAvailability();
}, []);

const fetchAvailability = async () => {
  const { data, error } = await supabase
    .from("activitydates")
    .select("*");

  if (error) {
    console.error(error);
    return;
  }

  const grouped = {};

  data.forEach(d => {
    if (!grouped[d.tour_id]) grouped[d.tour_id] = [];

    grouped[d.tour_id].push({
      id: d.id,
      startDate: d.start_date,
      endDate: d.end_date,
      isOpen: d.status === "open",
    });
  });

  setAvailability(grouped); // ✅ NOW EXISTS
};


  useEffect(() => {
  const loadAllDates = async () => {
    const { data, error } = await supabase
      .from("activitydates")
      .select("*");

    if (error) {
      console.error("Failed to load dates", error);
      return;
    }

    const grouped = {};

    data.forEach(d => {
      if (!grouped[d.tour_id]) grouped[d.tour_id] = [];

      grouped[d.tour_id].push({
        id: d.id,
        startDate: d.start_date,
        endDate: d.end_date,
        isOpen: d.status === "open",
      });
    });

    setAvailability(grouped);
  };

  loadAllDates();
}, []);


  useEffect(() => {
    // Save prices to localStorage
    const prices = {};
    tours.forEach(tour => {
      prices[tour.id] = { price: tour.price, originalPrice: tour.originalPrice };
    });
    localStorage.setItem('tourPrices', JSON.stringify(prices));
  }, [tours]);

  useEffect(() => {
    localStorage.setItem('tourBookings', JSON.stringify(bookings));
  }, [bookings]);

  const loginStaff = (password) => {
    if (password === STAFF_PASSWORD) {
      setIsStaffLoggedIn(true);
      return true;
    }
    return false;
  };

  const logoutStaff = () => {
    setIsStaffLoggedIn(false);
  };

  const addTourDate = (tourId, dateRange) => {
    setAvailability(prev => ({
      ...prev,
      [tourId]: [...(prev[tourId] || []), { ...dateRange, id: Date.now(), isOpen: true, slots: dateRange.slots || 10 }]
    }));
  };

const removeTourDate = async (tourId, dateId) => {
  await fetch(
    `${SUPABASE_URL}/rest/v1/tour_dates?id=eq.${dateId}`,
    {
      method: "DELETE",
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
      },
    }
  );
};


const toggleAvailability = async (tourId, dateId) => {
  const date = availability[tourId]?.find(d => d.id === dateId);
  if (!date) return;

  const newStatus = date.status === "open" ? "closed" : "open";

  const { error } = await supabase
    .from("activitydates")
    .update({ status: newStatus })
    .eq("id", dateId);

  if (error) {
    console.error(error);
    alert("Failed to update availability");
    return;
  }
};


const getOpenDates = (tourId) => {
  return availability[tourId]?.filter(d => d.isOpen) || [];
};


  const updateTourPrice = (tourId, newPrice, newOriginalPrice) => {
    setTours(prev => prev.map(tour => 
      tour.id === tourId 
        ? { ...tour, price: newPrice, originalPrice: newOriginalPrice }
        : tour
    ));
  };

  const addBooking = (booking) => {
    const newBooking = {
      ...booking,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      status: 'confirmed'
    };
    setBookings(prev => [newBooking, ...prev]);
    return newBooking;
  };

  const updateBookingStatus = (bookingId, status) => {
    setBookings(prev => prev.map(booking => 
      booking.id === bookingId ? { ...booking, status } : booking
    ));
  };

  return (
    <AppContext.Provider value={{
      tours,
      availability,
      bookings,
      isStaffLoggedIn,
      loginStaff,
      logoutStaff,
      fetchAvailability,
      addTourDate,
      removeTourDate,
      toggleAvailability,
      getOpenDates,
      updateTourPrice,
      addBooking,
      updateBookingStatus
    }}>
      {children}
    </AppContext.Provider>
  );
};
